// Ocean Insight Pro Mobile - Mock Data
// Gulf of Mexico and South Atlantic fishing regions

import {
  WeatherForecast,
  WeatherAlert,
  GeoCoordinate,
  WindData,
  WaveData,
  WeatherCondition,
} from '../types';
import { waypointService } from './waypointService';

// =============================================================================
// WAYPOINTS DATA
// =============================================================================

// Export an async function to get waypoints instead of a constant
export async function getWaypoints(): Promise<Array<GeoCoordinate & { name?: string; description?: string }>> {
  const { waypoints } = await waypointService.searchWaypoints({ limit: 10000 });
  return waypoints;
}

// For backwards compatibility, export an empty array that will be populated on first use
export const WAYPOINTS: Array<GeoCoordinate & { name?: string; description?: string }> = [];

// Convert waypoints to legacy format for compatibility
export const FISHING_REGIONS = {
  gulfOfMexico: {
    tampaBay: { latitude: 27.7676, longitude: -82.6403 },
    destin: { latitude: 30.3935, longitude: -86.4958 },
    venice: { latitude: 27.0997, longitude: -82.4544 },
    keyWest: { latitude: 24.5551, longitude: -81.7821 },
    galveston: { latitude: 29.3013, longitude: -94.7977 },
    pensacola: { latitude: 30.4518, longitude: -87.2169 },
    panama: { latitude: 30.1588, longitude: -85.8055 },
    orange: { latitude: 30.1296, longitude: -87.5678 },
  },
  southAtlantic: {
    charleston: { latitude: 32.7765, longitude: -79.9311 },
    savannah: { latitude: 32.0835, longitude: -81.0998 },
    jacksonville: { latitude: 30.3322, longitude: -81.6557 },
    capeCanaveral: { latitude: 28.3922, longitude: -80.6077 },
    stuart: { latitude: 27.1973, longitude: -80.2528 },
    palmBeach: { latitude: 26.7056, longitude: -80.0364 },
    fortLauderdale: { latitude: 26.1224, longitude: -80.1373 },
    miami: { latitude: 25.7617, longitude: -80.1918 },
  },
};

// =============================================================================
// MOCK WEATHER DATA GENERATORS
// =============================================================================

function generateWindData(): WindData {
  return {
    speed: Math.round(Math.random() * 25 + 5), // 5-30 knots
    direction: Math.round(Math.random() * 360),
    gusts: Math.round(Math.random() * 10 + 15), // 15-25 knots
  };
}

function generateWaveData(): WaveData {
  const height = Math.round((Math.random() * 6 + 1) * 10) / 10; // 1-7 feet
  return {
    height,
    period: Math.round(Math.random() * 8 + 4), // 4-12 seconds
    direction: Math.round(Math.random() * 360),
    swellHeight: Math.round(height * 0.7 * 10) / 10,
    swellPeriod: Math.round(Math.random() * 5 + 8), // 8-13 seconds
    swellDirection: Math.round(Math.random() * 360),
  };
}

function generateWeatherCondition(): WeatherCondition {
  const conditions = ['Clear', 'Partly Cloudy', 'Cloudy', 'Light Rain', 'Thunderstorms', 'Fog'];
  return {
    temperature: Math.round(Math.random() * 20 + 70), // 70-90°F
    humidity: Math.round(Math.random() * 40 + 60), // 60-100%
    pressure: Math.round((Math.random() * 2 + 29.8) * 100) / 100, // 29.8-31.8 inHg
    visibility: Math.round((Math.random() * 8 + 2) * 10) / 10, // 2-10 nm
    precipitation: Math.round(Math.random() * 2 * 100) / 100, // 0-2 inches
    conditions: conditions[Math.floor(Math.random() * conditions.length)],
  };
}

// =============================================================================
// 7-DAY WEATHER FORECAST MOCK DATA
// =============================================================================

export function generateWeatherForecast(
  location: GeoCoordinate,
  daysAhead: number = 7
): WeatherForecast[] {
  const forecasts: WeatherForecast[] = [];

  for (let i = 0; i < daysAhead; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    date.setHours(6, 0, 0, 0); // 6 AM each day

    forecasts.push({
      id: `forecast-${location.latitude}-${location.longitude}-${i}`,
      timestamp: date.toISOString(),
      location,
      wind: generateWindData(),
      waves: generateWaveData(),
      weather: generateWeatherCondition(),
      uvIndex: Math.round(Math.random() * 8 + 2), // UV index 2-10
      sunrise: new Date(date.getTime() + 6 * 60 * 60 * 1000).toISOString(), // 6 AM
      sunset: new Date(date.getTime() + 19 * 60 * 60 * 1000).toISOString(), // 7 PM
    });
  }

  return forecasts;
}

// =============================================================================
// CURRENT CONDITIONS FOR ALL REGIONS
// =============================================================================

export const CURRENT_WEATHER_CONDITIONS: WeatherForecast[] = [
  // Gulf of Mexico
  {
    id: 'current-tampa-bay',
    timestamp: new Date().toISOString(),
    location: FISHING_REGIONS.gulfOfMexico.tampaBay,
    wind: { speed: 12, direction: 180, gusts: 18 },
    waves: {
      height: 2.5,
      period: 6,
      direction: 185,
      swellHeight: 1.8,
      swellPeriod: 9,
      swellDirection: 190,
    },
    weather: {
      temperature: 82,
      humidity: 75,
      pressure: 30.15,
      visibility: 8.5,
      precipitation: 0,
      conditions: 'Partly Cloudy',
    },
    uvIndex: 7,
    sunrise: '2025-07-04T11:20:00.000Z',
    sunset: '2025-07-05T00:30:00.000Z',
  },
  {
    id: 'current-destin',
    timestamp: new Date().toISOString(),
    location: FISHING_REGIONS.gulfOfMexico.destin,
    wind: { speed: 8, direction: 135, gusts: 14 },
    waves: {
      height: 1.8,
      period: 5,
      direction: 140,
      swellHeight: 1.2,
      swellPeriod: 8,
      swellDirection: 145,
    },
    weather: {
      temperature: 85,
      humidity: 68,
      pressure: 30.22,
      visibility: 10,
      precipitation: 0,
      conditions: 'Clear',
    },
    uvIndex: 9,
    sunrise: '2025-07-04T11:15:00.000Z',
    sunset: '2025-07-05T00:35:00.000Z',
  },
  {
    id: 'current-key-west',
    timestamp: new Date().toISOString(),
    location: FISHING_REGIONS.gulfOfMexico.keyWest,
    wind: { speed: 15, direction: 90, gusts: 22 },
    waves: {
      height: 3.2,
      period: 7,
      direction: 95,
      swellHeight: 2.1,
      swellPeriod: 10,
      swellDirection: 100,
    },
    weather: {
      temperature: 88,
      humidity: 82,
      pressure: 30.08,
      visibility: 7.2,
      precipitation: 0.1,
      conditions: 'Light Rain',
    },
    uvIndex: 6,
    sunrise: '2025-07-04T11:25:00.000Z',
    sunset: '2025-07-05T00:25:00.000Z',
  },

  // South Atlantic
  {
    id: 'current-charleston',
    timestamp: new Date().toISOString(),
    location: FISHING_REGIONS.southAtlantic.charleston,
    wind: { speed: 10, direction: 210, gusts: 16 },
    waves: {
      height: 2.1,
      period: 6,
      direction: 215,
      swellHeight: 1.5,
      swellPeriod: 9,
      swellDirection: 220,
    },
    weather: {
      temperature: 79,
      humidity: 71,
      pressure: 30.18,
      visibility: 9.1,
      precipitation: 0,
      conditions: 'Partly Cloudy',
    },
    uvIndex: 8,
    sunrise: '2025-07-04T10:45:00.000Z',
    sunset: '2025-07-05T00:45:00.000Z',
  },
  {
    id: 'current-miami',
    timestamp: new Date().toISOString(),
    location: FISHING_REGIONS.southAtlantic.miami,
    wind: { speed: 18, direction: 110, gusts: 25 },
    waves: {
      height: 4.1,
      period: 8,
      direction: 115,
      swellHeight: 2.8,
      swellPeriod: 11,
      swellDirection: 120,
    },
    weather: {
      temperature: 86,
      humidity: 85,
      pressure: 29.98,
      visibility: 6.8,
      precipitation: 0.3,
      conditions: 'Thunderstorms',
    },
    uvIndex: 5,
    sunrise: '2025-07-04T11:30:00.000Z',
    sunset: '2025-07-05T00:20:00.000Z',
  },
];

// =============================================================================
// WEATHER ALERTS MOCK DATA
// =============================================================================

export const WEATHER_ALERTS: WeatherAlert[] = [
  {
    id: 'alert-small-craft-1',
    type: 'advisory',
    severity: 'moderate',
    title: 'Small Craft Advisory',
    description:
      'Winds 20-25 knots and seas 4-6 feet expected. Small craft should exercise caution.',
    startTime: new Date().toISOString(),
    endTime: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(), // 12 hours from now
    affectedAreas: [
      FISHING_REGIONS.gulfOfMexico.keyWest,
      FISHING_REGIONS.southAtlantic.miami,
      FISHING_REGIONS.southAtlantic.fortLauderdale,
    ],
  },
  {
    id: 'alert-thunderstorm-1',
    type: 'warning',
    severity: 'high',
    title: 'Severe Thunderstorm Warning',
    description:
      'Severe thunderstorms with winds over 35 knots, lightning, and heavy rain expected between 2-6 PM.',
    startTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
    endTime: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(), // 6 hours from now
    affectedAreas: [FISHING_REGIONS.gulfOfMexico.tampaBay, FISHING_REGIONS.gulfOfMexico.venice],
  },
  {
    id: 'alert-fog-1',
    type: 'advisory',
    severity: 'low',
    title: 'Dense Fog Advisory',
    description:
      'Dense fog reducing visibility to less than 1 nautical mile expected early morning hours.',
    startTime: new Date(Date.now() + 18 * 60 * 60 * 1000).toISOString(), // 18 hours from now (early morning)
    endTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
    affectedAreas: [
      FISHING_REGIONS.southAtlantic.charleston,
      FISHING_REGIONS.southAtlantic.savannah,
    ],
  },
];

// =============================================================================
// WEATHER QUALITY INDICATORS
// =============================================================================

export function getWeatherQuality(forecast: WeatherForecast): {
  overall: 'poor' | 'fair' | 'good' | 'excellent';
  factors: string[];
} {
  const factors: string[] = [];
  let score = 100;

  // Wind conditions
  if (forecast.wind.speed > 25) {
    score -= 30;
    factors.push('High winds');
  } else if (forecast.wind.speed > 20) {
    score -= 15;
    factors.push('Moderate winds');
  } else if (forecast.wind.speed < 5) {
    score -= 10;
    factors.push('Very light winds');
  }

  // Wave conditions
  if (forecast.waves.height > 6) {
    score -= 25;
    factors.push('Large waves');
  } else if (forecast.waves.height > 4) {
    score -= 15;
    factors.push('Moderate waves');
  }

  // Weather conditions
  if (forecast.weather.conditions.includes('Thunderstorms')) {
    score -= 40;
    factors.push('Thunderstorms');
  } else if (forecast.weather.conditions.includes('Rain')) {
    score -= 20;
    factors.push('Rain');
  } else if (forecast.weather.conditions.includes('Fog')) {
    score -= 25;
    factors.push('Low visibility');
  }

  // Visibility
  if (forecast.weather.visibility < 3) {
    score -= 20;
    factors.push('Poor visibility');
  } else if (forecast.weather.visibility < 5) {
    score -= 10;
    factors.push('Reduced visibility');
  }

  // Determine overall rating
  let overall: 'poor' | 'fair' | 'good' | 'excellent';
  if (score >= 85) overall = 'excellent';
  else if (score >= 70) overall = 'good';
  else if (score >= 50) overall = 'fair';
  else overall = 'poor';

  if (factors.length === 0) {
    factors.push('Excellent conditions');
  }

  return { overall, factors };
}

// =============================================================================
// EXPORT ALL WEATHER DATA
// =============================================================================

export const MOCK_WEATHER_DATA = {
  regions: FISHING_REGIONS,
  currentConditions: CURRENT_WEATHER_CONDITIONS,
  alerts: WEATHER_ALERTS,
  generateForecast: generateWeatherForecast,
  getQuality: getWeatherQuality,
};
